import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Sparkles, UserRound } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

/**
 * 建人物统一入口(0915 归一批 A)。
 *
 * ★为什么合并:此前库墙上并排两颗按钮 —— 「真人(老板 IP)」走采集流、「新建虚拟人物」开表单。
 * 两颗按钮把**一个决定**(要建谁)拆成了两条看起来不相干的路,而归一之后真人与虚拟人的差别
 * 只剩两处(授权存证、入库人脸检测),差两个字段不值得差两个入口。合并成一颗「新建人物」,
 * 第一步选类型,后续按类型分岔 —— 决定在前,流程在后。
 *
 * ★类型选择不做成下拉:两个选项各自需要一句话说清"什么时候选它"(真人要肖像授权、虚拟人不用),
 * 下拉塞不下这句话,而没有这句话选择就等于让用户猜。
 *
 * ★0916:两条路的形状对齐了 —— 虚拟人不再是"弹一个表单填完就完事",而是和真人一样
 * 走一条建档流(`/characters/new-virtual`):起名与设定 → 给它一张脸(传照片/视频,
 * 或让 AI 照着外观描述从零画)。老板 0916「新建虚拟人物也按照我们真人走,传照片和视频,
 * 这些可以让用户去生成,保证人物一致性」。`CharacterFormDialog` 退回它本来的位置:
 * **改**人物基本信息(详情页「编辑信息」),不再兼任建人物入口。
 */
export function CharacterCreateEntry({
  /** 触发按钮的形态:库墙页头用 default,空态用 outline 并排。 */
  variant = "default",
  /** 建完回哪(两条建档流共用的 returnTo);缺省各自落到新人物详情页。 */
  returnTo,
}: {
  variant?: "default" | "outline";
  returnTo?: string;
}) {
  const navigate = useNavigate();
  const [pickOpen, setPickOpen] = useState(false);

  const go = (path: string) => {
    setPickOpen(false);
    navigate(returnTo ? `${path}?returnTo=${encodeURIComponent(returnTo)}` : path);
  };

  return (
    <>
      <Button variant={variant} onClick={() => setPickOpen(true)} data-testid="character-create">
        <Plus className="mr-2 h-4 w-4" />
        新建人物
      </Button>

      <Dialog open={pickOpen} onOpenChange={setPickOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>建一个什么样的人物?</DialogTitle>
            {/* B2 #14(0927):此前写「两类人物建完之后用法完全一样」—— 但真人出镜页只收真人人物
                (VideoRealCreatePage 只取 isRealPerson),虚拟人进不了「谁来出镜」。产品口径:真人出镜线只收真人,
                虚拟人走分身舱 / 视频舱普通线。弹窗把这条差别说清,过滤不动。 */}
            <DialogDescription>
              两类人物照片和视频都能传,分身舱和视频舱普通线都能用;
              <b className="font-medium text-foreground">「真人出镜」只收真人人物</b>
              ,虚拟人不能出镜。另一处区别是建的时候要不要走授权。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <TypeCard
              icon={<UserRound className="h-5 w-5 text-primary" />}
              title="真人"
              desc="本人或同事出镜。需要肖像授权并留存证,素材入库时会做人脸体检。"
              cta="走采集流"
              testId="character-create-real"
              onClick={() => go("/characters/new-real")}
            />
            <TypeCard
              icon={<Sparkles className="h-5 w-5 text-primary" />}
              title="虚拟人"
              desc="动漫、插画等原创角色。没有肖像权人,不需要授权;可以直接让 AI 把它画出来。"
              cta="走建档流"
              testId="character-create-virtual"
              onClick={() => go("/characters/new-virtual")}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TypeCard({
  icon,
  title,
  desc,
  cta,
  testId,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  cta: string;
  testId: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex flex-col gap-2 rounded-lg border p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/55 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">{desc}</p>
      <span className="mt-auto pt-1 text-xs font-medium text-primary">{cta} →</span>
    </button>
  );
}
